import { useEffect, useRef, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { IcoX } from '@components/icons';

type Props = {
  courseId: string;
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
};

type TagResult = { id: string; name: string };

export function OntraportTagPicker({ courseId, selectedTags, onChange, placeholder }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TagResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasCredentials, setHasCredentials] = useState<boolean | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!courseId) {
      return;
    }
    rpx.campaigns
      .searchOntraportTags({ courseId })
      .then((r) => {
        setHasCredentials(r.hasCredentials);
        if (r.hasCredentials) {
          setResults(r.tags);
        }
      })
      .catch(() => setHasCredentials(false));
  }, [courseId]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSearch(value: string) {
    setQuery(value);
    if (!courseId) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await rpx.campaigns.searchOntraportTags({ courseId, query: value || undefined });
        setResults(r.tags);
        setShowDropdown(true);
      } catch {
        // Silently fail — user can still type freeform
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  function addTag(name: string) {
    const trimmed = name.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onChange([...selectedTags, trimmed]);
    }
    setQuery('');
    setShowDropdown(false);
  }

  function removeTag(name: string) {
    onChange(selectedTags.filter((t) => t !== name));
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) {
        addTag(query);
      }
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }

  if (hasCredentials === false) {
    return (
      <p class="text-xs text-gray-400 italic">No Ontraport account configured for this course.</p>
    );
  }

  const filtered = results.filter((r) => !selectedTags.includes(r.name));

  return (
    <div ref={wrapperRef} class="flex flex-col gap-2">
      {selectedTags.length > 0 && (
        <div class="flex flex-wrap gap-1.5">
          {selectedTags.map((tag) => (
            <span
              key={tag}
              class="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                class="hover:text-indigo-900 p-0.5"
              >
                <IcoX class="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div class="relative">
        <input
          type="text"
          value={query}
          onInput={(e) => handleSearch((e.target as HTMLInputElement).value)}
          onFocus={() => {
            if (filtered.length > 0) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Search tags by name'}
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        {loading && (
          <span class="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</span>
        )}
        {showDropdown && filtered.length > 0 && (
          <div class="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filtered.map((tag) => (
              <button
                key={tag.id}
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
                onClick={() => addTag(tag.name)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {hasCredentials !== undefined && (
        <p class="text-xs text-gray-400">
          Type to search Ontraport tags, or press Enter to add a custom tag.
        </p>
      )}
    </div>
  );
}
